import { useQueries } from '@tanstack/react-query';
import { queryKeys } from './query-client';
import { useAgentTaskInvalidation } from './agent-task-invalidation';
import { useWorkspaces } from './workspaces';
import { fetchWorkspaceTasks } from './workspace-tasks';
import { bucketTasks, type BucketableTask } from './agent-task-buckets';

// The Your Agents task strip's data — one row per top-level workspace with its agent-task
// counts per bucket, plus the live total. The web twin of native's `agent-workspace-tasks`
// bridge action, composed on the client rather than server-side.
//
// It reads the SAME per-workspace endpoint the workspace's own "Agents at work" cards read
// (`/api/workspace-tasks?workspaceId=`, already scoped to the workspace + one level of child
// projects) and buckets it through the SAME `bucketTasks`. That is the point: a chip here and
// the Agent Tasks tab it opens cannot disagree, and both share one React Query cache entry per
// workspace, so opening a workspace after this screen is a cache hit rather than a refetch.

export interface WorkspaceTaskCounts {
  id: string;
  slug: string;
  name: string;
  icon?: string;
  waiting: number;
  inProgress: number;
  done: number;
  failed: number;
}

export interface AgentWorkspaceTasks {
  workspaces: WorkspaceTaskCounts[];
  /** What the agents are holding right now — waiting + in progress across every workspace. */
  totalTasks: number;
  /**
   * Whether every workspace's task read has landed. `status: 'ready'` means the WORKSPACE LIST resolved;
   * the rows are best-effort from there, so a workspace still in flight reads as zero counts. A caller that
   * would mistake "not loaded yet" for "nothing is running" — the island's colour, and its "an agent started
   * working" baseline — waits on this instead.
   */
  settled: boolean;
}

export type AgentWorkspaceTasksState =
  | { status: 'loading' }
  | { status: 'ready'; data: AgentWorkspaceTasks }
  | { status: 'error' };

/**
 * One workspace's row: its identity plus a count per bucket.
 *
 * EVERY workspace gets a row, including one with nothing in it — the strip is the complete
 * list of places the agents work, in the roster's own order, so it reads like the sidebar
 * rather than a shifting "what's hot" list. A workspace with no tasks says so.
 */
export function workspaceTaskRow(
  workspace: Pick<WorkspaceTaskCounts, 'id' | 'slug' | 'name' | 'icon'>,
  tasks: BucketableTask[],
): WorkspaceTaskCounts {
  const byBucket = bucketTasks(tasks);
  return {
    id: workspace.id,
    slug: workspace.slug,
    name: workspace.name,
    icon: workspace.icon,
    waiting: byBucket.waiting.length,
    inProgress: byBucket.inProgress.length,
    done: byBucket.done.length,
    failed: byBucket.failed.length,
  };
}

/** Whether a row has anything at all — an empty workspace shows a "no tasks" chip instead of none. */
export function rowIsEmpty(row: WorkspaceTaskCounts): boolean {
  return row.waiting + row.inProgress + row.done + row.failed === 0;
}

/** The headline count: what the agents are holding right now, not what they have finished. */
export function totalLiveTasks(rows: WorkspaceTaskCounts[]): number {
  return rows.reduce((sum, row) => sum + row.waiting + row.inProgress, 0);
}

/**
 * The island's two numbers, summed across every workspace: in-motion (the in-progress bucket — tasks
 * open now) and waiting on the user (the waiting bucket). Mirrors the native derivation off the
 * server-aggregated per-workspace counts, so the pill reads identically on both surfaces.
 *
 * `failed` is NOT part of the waiting count, and this comment used to promise parity while the code
 * broke it. Native sums `waiting` alone from the same aggregate — iOS `SymphonyData.islandWaitingCount`
 * ("no in-progress, no failed"), Android `Models.kt islandWaitingCount`, whose `failed` field is
 * documented "NOT counted in the island's waiting count ... it feeds the Failed tab only". Adding
 * `row.failed` here made one account read orange on web and green on a phone.
 *
 * It also made the pill impossible to clear. A FAILED task is finished, so no user action discharges it,
 * and `ManageTask` rightly refuses to re-close a terminal task (that would overwrite the summary its card
 * shows). So the pill demanded an action nothing could satisfy, while every query the agent ran returned
 * zero and `world.md` said nothing was waiting. Failures keep their own bucket and their own Failed tab.
 */
export function islandCountsFromRows(rows: WorkspaceTaskCounts[]): { inMotionCount: number; waitingCount: number } {
  return rows.reduce(
    (acc, row) => ({
      inMotionCount: acc.inMotionCount + row.inProgress,
      waitingCount: acc.waitingCount + row.waiting,
    }),
    { inMotionCount: 0, waitingCount: 0 },
  );
}

export function useAgentWorkspaceTasks(): AgentWorkspaceTasksState {
  const workspacesState = useWorkspaces();
  const workspaces = workspacesState.status === 'ready' ? workspacesState.workspaces : [];

  const queries = useQueries({
    queries: workspaces.map((workspace) => ({
      queryKey: queryKeys.workspaceTasks(workspace.id),
      queryFn: () => fetchWorkspaceTasks(workspace.id),
    })),
  });

  // NOT a per-instance `refetch()` fan-out. This hook mounts four times on the home screen alone (the
  // island badge, `useIslandState`, and twice through `useIslandAgentStarted`), and each instance used to
  // register its own duplexer listener that refetched EVERY workspace entry — one frame, four fan-outs
  // over the same shared keys, cancelling each other because `refetch()` defaults to `cancelRefetch: true`.
  // One shared listener invalidates the `workspace-tasks` family once; React Query refetches each active
  // entry once. See `lib/agent-task-invalidation.ts`.
  useAgentTaskInvalidation();

  if (workspacesState.status === 'error') return { status: 'error' };
  if (workspacesState.status === 'loading') return { status: 'loading' };

  // A workspace whose tasks are still in flight, or whose read failed, counts zero rather than
  // holding the whole strip back — the same best-effort-per-workspace contract as native. It still
  // gets its card; only its chips are missing.
  const rows = workspaces.map((workspace, i) => workspaceTaskRow(workspace, queries[i]?.data ?? []));
  // A read that failed counts as settled: it is never coming, and holding the island unpainted on it would
  // hide the pill for the whole session. An account with no workspaces has nothing to wait for.
  const settled = queries.every((query) => query.isSuccess || query.isError);

  return { status: 'ready', data: { workspaces: rows, totalTasks: totalLiveTasks(rows), settled } };
}
